import { isNil, last } from 'lodash';
import { CollectionViewSentryContext, LongTaskMonitorDeps, LongTaskThresholdResult } from './types';

/** Single long task at or above this duration triggers a warning event. */
export const LONG_TASK_SINGLE_THRESHOLD_MS = 1_000;

/** Rolling window length used to report cumulative long-task duration as context. */
export const LONG_TASK_WINDOW_MS = 10_000;

/**
 * Closed hidden-tab intervals older than this are pruned. Long tasks that
 * overlap a hidden interval are delivered by the observer right after the tab
 * is refocused, so a short retention window is sufficient.
 */
export const HIDDEN_INTERVAL_RETENTION_MS = 60_000;

/**
 * Buckets a long-task duration so Sentry groups reports by severity instead of
 * collapsing them into a single noisy issue. Returns undefined for durations
 * below LONG_TASK_SINGLE_THRESHOLD_MS, which we intentionally do not report.
 */
export function getLongTaskDurationBucket(durationMs: number): string | undefined {
  if (durationMs >= 4_000) {
    return '4000ms+';
  }
  if (durationMs >= 3_000) {
    return '3000-4000ms';
  }
  if (durationMs >= 2_000) {
    return '2000-3000ms';
  }
  if (durationMs >= LONG_TASK_SINGLE_THRESHOLD_MS) {
    return '1000-2000ms';
  }
  return undefined;
}

/** Step of the fine-grained duration tag used for approximating percentiles. */
export const LONG_TASK_FINE_BUCKET_STEP_MS = 250;

/** Fine-grained durations at or above this collapse into one open-ended bucket. */
export const LONG_TASK_FINE_BUCKET_MAX_MS = 10_000;

/**
 * Fine-grained variant of the duration bucket, tagged on reports so Discover
 * can approximate per-release duration percentiles from the bucket histogram
 * (the errors dataset cannot aggregate raw numeric values). Zero-padded so the
 * tag values sort correctly as strings; the open-ended top bucket ("10000+")
 * sorts after every padded value.
 */
export function getLongTaskFineDurationBucket(durationMs: number): string | undefined {
  if (durationMs < LONG_TASK_SINGLE_THRESHOLD_MS) {
    return undefined;
  }
  if (durationMs >= LONG_TASK_FINE_BUCKET_MAX_MS) {
    return `${LONG_TASK_FINE_BUCKET_MAX_MS}+`;
  }
  const flooredMs =
    Math.floor(durationMs / LONG_TASK_FINE_BUCKET_STEP_MS) * LONG_TASK_FINE_BUCKET_STEP_MS;
  return String(flooredMs).padStart(5, '0');
}

interface LongTaskWindowEntry {
  startMs: number;
  durationMs: number;
}

interface HiddenInterval {
  startMs: number;
  endMs?: number;
}

export function isLongTaskMonitoringSupported(): boolean {
  if (typeof PerformanceObserver === 'undefined') {
    return false;
  }

  return PerformanceObserver.supportedEntryTypes?.includes('longtask') ?? false;
}

export function evaluateLongTaskThreshold(
  durationMs: number,
  entries: LongTaskWindowEntry[],
  nowMs: number,
): LongTaskThresholdResult {
  const recentEntries = entries.filter(entry => nowMs - entry.startMs <= LONG_TASK_WINDOW_MS);
  const windowTotalMs = recentEntries.reduce((sum, entry) => sum + entry.durationMs, 0);

  return {
    // Only single tasks at or above the threshold are reported. Accumulated
    // sub-threshold tasks are intentionally ignored to keep report volume down.
    shouldCapture: durationMs >= LONG_TASK_SINGLE_THRESHOLD_MS,
    windowTotalMs,
    singleTaskMs: durationMs,
  };
}

export class LongTaskMonitor {
  private stopMonitoring: (() => void) | undefined;
  private stopVisibilityTracking: (() => void) | undefined;
  private readonly windowEntries: LongTaskWindowEntry[] = [];
  private readonly hiddenIntervals: HiddenInterval[] = [];
  private lastPathname = '';
  private hasCapturedWarningThisPage = false;
  private collectionViewContext: CollectionViewSentryContext | undefined;

  constructor(private readonly deps: LongTaskMonitorDeps) {}

  start() {
    this.stop();

    if (!isLongTaskMonitoringSupported()) {
      return;
    }

    this.startVisibilityTracking();

    try {
      const observer = new PerformanceObserver(list => {
        for (const entry of list.getEntries()) {
          this.handleLongTask(entry);
        }
      });

      observer.observe({ type: 'longtask', buffered: true });
      this.stopMonitoring = () => observer.disconnect();
    } catch {
      this.stopMonitoring = undefined;
    }
  }

  stop() {
    this.stopMonitoring?.();
    this.stopMonitoring = undefined;
    this.stopVisibilityTracking?.();
    this.stopVisibilityTracking = undefined;
    this.hiddenIntervals.length = 0;
  }

  setCollectionViewContext(context: CollectionViewSentryContext) {
    this.collectionViewContext = context;
  }

  clearCollectionViewContext() {
    this.collectionViewContext = undefined;
  }

  private startVisibilityTracking() {
    if (typeof document === 'undefined') {
      return;
    }

    if (document.visibilityState === 'hidden') {
      this.hiddenIntervals.push({ startMs: performance.now() });
    }

    const onVisibilityChange = () => this.handleVisibilityChange();
    document.addEventListener('visibilitychange', onVisibilityChange);
    this.stopVisibilityTracking = () =>
      document.removeEventListener('visibilitychange', onVisibilityChange);
  }

  private handleVisibilityChange() {
    const nowMs = performance.now();

    if (document.visibilityState === 'hidden') {
      this.hiddenIntervals.push({ startMs: nowMs });
      return;
    }

    const openInterval = last(this.hiddenIntervals);
    if (openInterval && isNil(openInterval.endMs)) {
      openInterval.endMs = nowMs;
    }
  }

  private overlapsHiddenInterval(taskStartMs: number, taskEndMs: number): boolean {
    return this.hiddenIntervals.some(
      interval => taskStartMs < (interval.endMs ?? Infinity) && taskEndMs > interval.startMs,
    );
  }

  private pruneHiddenIntervals(nowMs: number) {
    const retained = this.hiddenIntervals.filter(
      interval => isNil(interval.endMs) || nowMs - interval.endMs <= HIDDEN_INTERVAL_RETENTION_MS,
    );
    this.hiddenIntervals.length = 0;
    this.hiddenIntervals.push(...retained);
  }

  private handleLongTask(entry: PerformanceEntry) {
    const durationMs = entry.duration;
    const startMs = entry.startTime;
    const nowMs = performance.now();
    const pathname = window.location.pathname;

    this.pruneHiddenIntervals(nowMs);

    // A long task that overlaps a hidden-tab interval is a measurement
    // artifact: the browser can report the entire throttled background period
    // as one task.
    if (this.overlapsHiddenInterval(startMs, startMs + durationMs)) {
      return;
    }

    if (pathname !== this.lastPathname) {
      this.lastPathname = pathname;
      this.hasCapturedWarningThisPage = false;
    }

    this.windowEntries.push({ startMs, durationMs });

    const recentEntries = this.windowEntries.filter(
      item => nowMs - item.startMs <= LONG_TASK_WINDOW_MS,
    );
    this.windowEntries.length = 0;
    this.windowEntries.push(...recentEntries);

    const threshold = evaluateLongTaskThreshold(durationMs, this.windowEntries, nowMs);

    this.deps.breadcrumb({
      category: 'performance',
      message: 'longtask',
      data: { durationMs, startMs, pathname, windowTotalMs: threshold.windowTotalMs },
    });

    if (!threshold.shouldCapture || this.hasCapturedWarningThisPage) {
      return;
    }

    this.hasCapturedWarningThisPage = true;
    this.deps.captureLongTaskWarning({
      durationMs,
      startMs,
      pathname,
      windowTotalMs: threshold.windowTotalMs,
      collectionViewContext: this.collectionViewContext,
    });
  }
}
